import { useState, useEffect, useMemo } from "react";
import DataTable from "../../lib/components/DataTable";
import { getColumns } from "./constant.js";
import Pagination from "../../lib/components/Pagination/Pagination.jsx";
import useFleetSearchReducer from "./hooks/useFleetSearchHook/useFleetSearchHook.js";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider.jsx";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel.jsx";
import MultiSelect from "../../lib/components/MultiSelect/MultiSelect.jsx";
import { Settings } from "lucide-react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { matchesColumnFilter } from "../../lib/utils/tableUtils";

export default function FleetMetrics() {
  const { serialNumber, siteId, setState } = useGlobalFilters();
  const [allTableData, setAllTableData] = useState([]); // All data from API
  const fleetSearchDetails = useFleetSearchReducer();
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const navigate = useNavigate();

  // Filter and sort state (lifted from DataTable)
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Visible columns: manage in this page (DataTable is dumb and just respects it)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Show a small, important subset by default
    const defaultVisibleKeys = ["serialNumber", "siteId", "displayName", "country", "fwVersion", "isConnected"];
    return getColumns().reduce((acc, col) => {
      acc[col.key] = defaultVisibleKeys.includes(col.key);
      return acc;
    }, {});
  });

  // Ensure new columns get an entry in visibleColumns
  useEffect(() => {
    setVisibleColumns(prev => {
      const next = { ...prev };
      const defaultVisibleKeys = ["serialNumber", "siteId", "displayName", "country", "fwVersion", "isConnected"];
      getColumns().forEach(col => {
        if (!(col.key in next)) {
          next[col.key] = defaultVisibleKeys.includes(col.key);
        }
      });
      return next;
    });
  }, []);

  const handleColumnVisibilityToggle = key => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    const incoming = Array.isArray(fleetSearchDetails?.state?.data) ? fleetSearchDetails.state.data : [];
    setAllTableData(incoming);
    // Reset pagination and filters when new data comes in
    setPage(0);
    setFilters({});
    setSortConfig({ key: null, direction: "asc" });
  }, [fleetSearchDetails?.state?.data]);

  const columns = getColumns((serialNumber, siteId) => {
    setState({ summarySerialNumber: serialNumber, summarySiteId: siteId });
    navigate(`/summary`);
  });

  // Step 1: Filter ALL data
  const filteredData = useMemo(() => {
    if (!allTableData || allTableData.length === 0) return [];

    return allTableData.filter(row => columns.every(col => matchesColumnFilter(col, row, filters)));
  }, [allTableData, columns, filters]);

  // Step 2: Sort filtered data
  const sortedAndFilteredData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    if (!sortConfig.key) return filteredData;

    let result = [...filteredData];

    result.sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Convert to comparable values
      let compareA = aValue;
      let compareB = bValue;

      // If both are numbers, compare as numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        compareA = aValue;
        compareB = bValue;
      }
      // If both are booleans, compare as booleans
      else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        compareA = aValue ? 1 : 0;
        compareB = bValue ? 1 : 0;
      }
      // If both are strings, try date parsing first, then string comparison
      else if (typeof aValue === "string" && typeof bValue === "string") {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        // If both are valid dates, compare as dates
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          compareA = dateA.getTime();
          compareB = dateB.getTime();
        } else {
          // Otherwise, compare as strings (case-insensitive)
          compareA = aValue.toLowerCase();
          compareB = bValue.toLowerCase();
        }
      }
      // Mixed types - convert to string for comparison
      else {
        compareA = String(aValue).toLowerCase();
        compareB = String(bValue).toLowerCase();
      }

      // Compare values
      if (compareA < compareB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (compareA > compareB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [filteredData, sortConfig]);

  // Step 3: Paginate sorted and filtered data
  const totalItems = sortedAndFilteredData?.length || 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 1;
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = sortedAndFilteredData.slice(startIndex, endIndex);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
    setPage(0); // Reset to first page when sorting changes
  };

  const handleFilter = newFilters => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  return (
    <div className="w-full h-screen overflow-scroll p-8">
      <h1 className="text-2xl font-bold mb-2">Fleet Search</h1>
      <div>
        <div className="mt-4">
          {/* Column visibility selector (typeahead multi-select) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <MultiSelect
              options={columns}
              selectedValues={visibleColumns}
              onSelectionChange={handleColumnVisibilityToggle}
              buttonLabel="Show Columns"
              searchPlaceholder="Search columns..."
              emptyMessage="No columns found"
              buttonIcon={<Settings size={16} />}
              getOptionLabel={option => option.label}
              getOptionId={option => option.key}
            />
          </Box>

          <DataTable
            columns={columns}
            data={paginatedData}
            visibleColumns={visibleColumns}
            onSort={handleSort}
            onFilter={handleFilter}
            loading={fleetSearchDetails?.state?.isLoading}
          />
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalItems={totalItems}
          />
        </div>
        {openChargerDetailsSlideout && (
          <SlideOutPanel
            open={openChargerDetailsSlideout}
            onClose={() => setOpenChargerDetailsSlideout(false)}
            serialNumber={serialNumber}
            siteId={siteId}
          />
        )}
      </div>
    </div>
  );
}
